import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CashFlowOffer } from '@/lib/cashFlowDetail';
import { formatEur, formatEtDate, type MoneyMode } from '@/lib/cashFlow';
import { cn } from '@/lib/utils';
import {
  supplierKey,
  offerPaletteColor,
  injectOfferHighlight,
  buildOfferPaintMessage,
  isOfferReadyMessage,
} from '@/lib/offerHighlight';

/**
 * PAKKUMISE VAATAJA (module 1.3, the rich offer view — Part B) — a full-screen
 * overlay opened from the enriched detail's "Seotud pakkumine" row. LEFT: the
 * supplier breakdown finance-core computed (groups + nets — read, never recomputed;
 * master rule / L9). RIGHT: the REAL sanitized Erply printout in an
 * `iframe sandbox="allow-scripts"` (never `allow-same-origin`) with the highlight
 * engine injected. Hovering / colour-marking / ✓-confirming a supplier repaints
 * the matching printout rows via `postMessage`; the parent trusts the iframe's
 * `ready` handshake ONLY from that one window (the security boundary). The
 * highlight state is a VIEW-ONLY review aid (persists nothing). Portalled to
 * document.body so a backdrop-filter/transform ancestor can't re-anchor the fixed
 * overlay. The invoice's own DocumentPane is untouched.
 */
export interface OfferViewerProps {
  offer: CashFlowOffer;
  onClose: () => void;
}

export function OfferViewer({ offer, onClose }: OfferViewerProps) {
  const [mode, setMode] = useState<MoneyMode>('net');
  const [marked, setMarked] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(() => new Set());
  const [target, setTarget] = useState<Window | null>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const html = offer.document?.html ?? null;
  const srcDoc = useMemo(() => (html === null ? null : injectOfferHighlight(html)), [html]);

  // Trust the injected engine's "ready" handshake ONLY from our iframe's window,
  // then remember that window as the paint target (so no later null-guard is a
  // dead branch — a ready target always has a live window).
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const win = iframeRef.current?.contentWindow ?? null;
      if (e.source !== win) return;
      if (isOfferReadyMessage(e.data)) setTarget(win);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Repaint the printout for the current view state whenever it changes.
  useEffect(() => {
    if (target === null) return;
    target.postMessage(buildOfferPaintMessage(offer.groups, { hovered, marked, confirmed }), '*');
  }, [target, offer.groups, hovered, marked, confirmed]);

  const amounts = offer.amounts;
  const grandCents = amounts === null ? null : mode === 'net' ? amounts.netCents : amounts.grossCents;

  const toggleConfirm = (key: string) =>
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return createPortal(
    <div
      data-testid="offer-viewer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Pakkumise vaataja"
      onClick={onClose}
      className="fixed inset-0 z-[60] flex bg-background/80 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="m-auto flex h-[92vh] w-[min(90rem,96vw)] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
          <span className="shrink-0 rounded border border-[hsl(var(--primary))]/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
            Pakkumine
          </span>
          <span className="font-mono text-sm font-semibold text-foreground">{offer.docNumber ?? '—'}</span>
          <span className="text-[12px] text-muted-foreground">
            {offer.documentDate !== null && `${formatEtDate(offer.documentDate)} · `}
            {offer.party}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            aria-label="Sulge"
            onClick={onClose}
            className="rounded border border-border px-2 py-0.5 text-muted-foreground hover:bg-accent"
          >
            <span aria-hidden>×</span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 border-b border-border bg-card px-4 py-2">
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(['net', 'gross'] as MoneyMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'px-3 py-1 text-[12px] font-semibold transition-colors',
                  mode === m
                    ? 'bg-[hsl(var(--primary))] text-white'
                    : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {m === 'net' ? 'KM-ta' : 'KM-ga'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMarked((v) => !v)}
            className={cn(
              'rounded-md border px-3 py-1 text-[12px] font-semibold transition-colors',
              marked
                ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                : 'border-border text-foreground hover:bg-accent',
            )}
          >
            {marked ? 'Eemalda märgistus' : 'Märgi tarnijad'}
          </button>
          <span className="text-[12px] text-muted-foreground">Hõlju tarnijal → esiletõst väljatrükil</span>
          <span className="flex-1" />
          <span className="text-[12px] text-muted-foreground">
            Pakkumine kokku ({mode === 'net' ? 'KM-ta' : 'KM-ga'})
          </span>
          <span className="font-mono text-sm font-semibold text-foreground">
            {grandCents === null ? '—' : formatEur(grandCents)}
          </span>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1">
          {/* Left: supplier breakdown (finance-core) */}
          <aside className="flex w-[24rem] shrink-0 flex-col border-r border-border">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <span>Tarnijad · neto</span>
              <span>{`${offer.groups.length} ${offer.groups.length === 1 ? 'tarnija' : 'tarnijat'}`}</span>
            </div>
            <div className="flex flex-1 flex-col gap-2 overflow-auto p-2.5">
              {offer.groups.map((group, index) => {
                const key = supplierKey(group.supplierExternalId);
                const color = offerPaletteColor(index);
                const isConfirmed = confirmed.has(key);
                const isHovered = hovered === key;
                const isUnassigned = group.supplierExternalId === null;
                return (
                  <div
                    key={key}
                    onMouseEnter={() => setHovered(key)}
                    onMouseLeave={() => setHovered(null)}
                    className={cn(
                      'rounded-md border px-3 py-2.5 transition-colors',
                      isHovered ? 'border-border bg-accent' : 'border-border',
                      isConfirmed && 'outline outline-1 outline-[hsl(var(--income))]',
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={cn('h-3 w-3 shrink-0 rounded-sm', !marked && 'border border-border')}
                        style={marked ? { backgroundColor: color.swatch } : undefined}
                      />
                      <span
                        className={cn(
                          'flex-1 truncate text-[13px] font-semibold',
                          isUnassigned ? 'italic text-muted-foreground' : 'text-foreground',
                        )}
                      >
                        {group.supplierName ?? 'Tarnija määramata'}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{group.lineCount} rida</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono text-[13px] font-semibold text-foreground">
                        {formatEur(group.netCents)}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleConfirm(key)}
                        className={cn(
                          'rounded border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                          isConfirmed
                            ? 'border-[hsl(var(--income))] bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]'
                            : 'border-border text-muted-foreground hover:bg-accent',
                        )}
                      >
                        {isConfirmed ? '✓ Kinnitatud' : 'Kinnita'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-baseline justify-between border-t border-border px-4 py-3">
              <span className="text-[12px] text-muted-foreground">Netosumma</span>
              <span className="font-mono text-[15px] font-bold text-foreground">
                {amounts === null ? '—' : formatEur(amounts.netCents)}
              </span>
            </div>
          </aside>

          {/* Right: real sanitized Erply printout (or its cache-state fallback) */}
          <div className="min-w-0 flex-1 bg-[hsl(222_30%_6%)] p-4">
            {srcDoc === null ? (
              <OfferPrintoutFallback document={offer.document} />
            ) : (
              <iframe
                ref={iframeRef}
                title={`Erply pakkumine ${offer.docNumber ?? ''}`.trim()}
                srcDoc={srcDoc}
                sandbox="allow-scripts"
                className="h-full w-full rounded border-none bg-white"
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Shown in the printout pane when the sanitized HTML is not yet mirrored — a
 *  loading state while the sync is pending, or an error state, with an "Ava
 *  originaalis" new-tab escape hatch to the vendor original when we have its URL. */
function OfferPrintoutFallback({ document }: { document: CashFlowOffer['document'] }) {
  const isError = document?.cacheStatus === 'error';
  const externalUrl = document?.externalUrl ?? null;
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded bg-white/[0.02] text-center">
      <p className="text-[13px] text-muted-foreground">
        {isError ? 'Väljatrüki laadimine ebaõnnestus.' : 'Väljatrükki laaditakse…'}
      </p>
      {externalUrl !== null && (
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md border border-border px-3 py-1.5 text-[12px] font-semibold text-[hsl(var(--primary))] hover:bg-accent"
        >
          Ava originaalis →
        </a>
      )}
    </div>
  );
}
